import { EarningsPeriod } from '@api/api/earnings';
import { Formats, formatDateToLocale } from '@helpers/utils/dates/dates';
import { useLanguageStore } from '@terminal/stores/use-language-store/useLanguageStore';
import { formatNumberWithUnit } from '@terminal/widgets/earnings/utils/utils';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import styles from './EarningsInsightSection.module.scss';

export interface PreviewContentProps {
  earningsData: EarningsPeriod;
  metricLabelClassName?: string;
}

export const PreviewContent = ({
  earningsData,
  metricLabelClassName,
}: PreviewContentProps) => {
  const { t } = useTranslation(['widget']);
  const languageSetting = useLanguageStore(state => state.languageSetting);

  const formattedDate = earningsData.reporting_date
    ? formatDateToLocale(
        languageSetting,
        new Date(earningsData.reporting_date),
        Formats['MMMM d']
      )
    : '-';

  const reportPeriod =
    earningsData.fiscal_period && earningsData.fiscal_year
      ? `${earningsData.fiscal_period} ${earningsData.fiscal_year}`
      : '-';
  const metricLabelClasses = clsx(styles.metricLabel, metricLabelClassName);

  return (
    <>
      <div className={styles.metricContainer}>
        <span className={metricLabelClasses}>
          {t('widget:assetOverview.earningsSection.nextReportDate')}
        </span>
        <span className={styles.metricValue}>{formattedDate}</span>
      </div>
      <div className={styles.metricContainer}>
        <span className={metricLabelClasses}>
          {t('widget:assetOverview.earningsSection.reportPeriod')}
        </span>
        <span className={styles.metricValueMono}>{reportPeriod}</span>
      </div>
      <div className={styles.metricContainer}>
        <span className={metricLabelClasses}>
          {t('widget:assetOverview.earningsSection.epsEstimate')}
        </span>
        <span className={styles.metricValueMono}>
          {formatNumberWithUnit(earningsData.eps?.estimate)}
          <span className={styles.currencyLabel}>
            {earningsData.currency ?? 'USD'}
          </span>
        </span>
      </div>
      <div className={styles.metricContainer}>
        <span className={metricLabelClasses}>
          {t('widget:assetOverview.earningsSection.revenueEstimate')}
        </span>
        <span className={styles.metricValueMono}>
          {formatNumberWithUnit(earningsData.revenue?.estimate)}
          <span className={styles.currencyLabel}>
            {earningsData.currency ?? 'USD'}
          </span>
        </span>
      </div>
    </>
  );
};
